// Minimal hyperscript helper — avoids pulling in a framework.

type Child = Node | string | number | null | undefined | false;

export function h(
  tag: string,
  attrs: Record<string, unknown> = {},
  ...children: (Child | Child[])[]
): HTMLElement {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === null || v === undefined || v === false) continue;
    if (k === "class") e.className = String(v);
    else if (k === "html") e.innerHTML = String(v);
    else if (k.startsWith("on") && typeof v === "function") {
      e.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
    } else if (k === "value") (e as HTMLInputElement).value = String(v);
    else e.setAttribute(k, String(v));
  }
  for (const c of children.flat()) {
    if (c === null || c === undefined || c === false) continue;
    e.append(c instanceof Node ? c : document.createTextNode(String(c)));
  }
  return e;
}

export function clear(node: HTMLElement): void {
  node.replaceChildren();
}

export function field(
  label: string,
  input: HTMLElement,
  hint?: string,
): HTMLElement {
  return h("label", { class: "field" }, h("span", { class: "field-label" }, label), input,
    hint ? h("span", { class: "field-hint" }, hint) : null);
}

// commit "input" fires on every keystroke (use when onChange only mutates a
// local object and does NOT re-render). commit "change" fires on blur/enter —
// use when onChange triggers a re-render, so typing isn't interrupted and the
// mobile keyboard stays open.
export function numberInput(
  value: number,
  onChange: (v: number) => void,
  step = "1000",
  commit: "input" | "change" = "input",
): HTMLInputElement {
  const handler = (e: Event) => onChange(Number((e.target as HTMLInputElement).value) || 0);
  const attrs: Record<string, unknown> = {
    type: "number",
    // 0 renders blank: a blank field reads as "not entered", which is what 0
    // means in these inputs (UX_FINDINGS U-14). onChange maps blank back to 0.
    value: value ? String(value) : "",
    step,
    min: "0",
    inputmode: "decimal",
  };
  attrs[commit === "change" ? "onchange" : "oninput"] = handler;
  const inp = h("input", attrs) as HTMLInputElement;
  return inp;
}

// Money input: a plain text box that formats the number with thousands commas
// as you type, preserving the caret. Integer SGD amounts only. `onChange` fires
// live on input; `commit: "change"` restricts it to blur/Enter instead.
export function moneyInput(
  value: number,
  onChange: (v: number) => void,
  commit: "input" | "change" = "input",
): HTMLInputElement {
  // 0 renders blank (see numberInput); typing nothing still commits 0.
  const fmt = (n: number) => (n > 0 ? n.toLocaleString("en-US") : "");
  const inp = h("input", {
    type: "text", inputmode: "numeric", value: fmt(value), class: "money-box",
  }) as HTMLInputElement;

  const reformat = () => {
    const prevCaret = inp.selectionStart ?? inp.value.length;
    const digitsBeforeCaret = inp.value.slice(0, prevCaret).replace(/\D/g, "").length;
    const digits = inp.value.replace(/\D/g, "");
    const n = digits ? parseInt(digits, 10) : 0;
    const formatted = digits ? n.toLocaleString("en-US") : "";
    inp.value = formatted;
    // Restore caret after the same number of digits.
    let pos = 0;
    let seen = 0;
    while (pos < formatted.length && seen < digitsBeforeCaret) {
      if (/\d/.test(formatted[pos])) seen++;
      pos++;
    }
    inp.setSelectionRange(pos, pos);
    return n;
  };

  // Always reformat visually while typing; emit the value per the commit mode.
  inp.addEventListener("input", () => { const n = reformat(); if (commit === "input") onChange(n); });
  if (commit === "change") inp.addEventListener("change", () => onChange(reformat()));
  return inp;
}
